import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
  disableBodyScroll,
  enableBodyScroll,
} from '@hh/global.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
    animateOpen: {
      reflect: true,
    },
    transitionDelay: {
      reflect: true,
    },
    background: {
      type: String,
    },
    showClose: {
      type: String,
      attribute: 'show-close',
    },
    lockScroll: {
      type: Boolean,
      attribute: 'lock-scroll',
    },
    customLockScroll: {
      type: Boolean,
      attribute: 'custom-lock-scroll',
    },
    noPauseMedia: {
      type: Boolean,
      attribute: 'no-pause-media',
    }
  }

  constructor() {
    super()
    this.background = false
    this.showClose = 'true'
    this.lockScroll = false
    this.customLockScroll = false
    this.noPauseMedia = false
    this.open = false
    this.transitionDelay = 0
    this.handleBackgroundClick = this.handleBackgroundClick.bind(this)
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name] && this.noPauseMedia !== true) {
      utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.transitionDelay = this.getTransitionTimeMs()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })

    if (this.background) {
      this.querySelector(`.${this.background}`)?.addEventListener(
        'click',
        this.handleBackgroundClick
      )
    }
  }

  handleBackgroundClick(e) {
    this.close(e)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.receiver?.disconnect()

    if (this.background) {
      this.querySelector(`.${this.background}`)?.removeEventListener(
        'click',
        this.handleBackgroundClick
      )
    }
  }

  openFromDocument(data) {
    if (this.id !== data.id) {
      this.animateOpen = false

      setTimeout(() => {
        this.open = false
      }, this.transitionDelay)

      return
    }

    this.open = true
    this.animateOpen = true

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }

    setTimeout(() => {
      const elementToFocus = this.renderRoot?.querySelector('[role="dialog"]') ?? null
      elementToFocus?.focus();
    }, this.transitionDelay)
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      this[`${this.open ? 'add' : 'remove'}BodyListener`]()
    }
  }

  addBodyListener() {
    if (this.customLockScroll) {
      const html = document.querySelector('html')
      html.classList.add('body-lock')
      document.body.classList.add('body-lock')
    }
    if (this.lockScroll) {
      disableBodyScroll(this, {
        allowTouchMove: (el) => {
          while (el && el !== document.body) {
            if (el.getAttribute('body-scroll-lock-ignore') !== null) {
              return true
            }

            el = el.parentElement
          }
        },
      })
    }
  }

  removeBodyListener() {
    if (this.customLockScroll) {
      const html = document.querySelector('html')
      html.classList.remove('body-lock')
      document.body.classList.remove('body-lock')
    }
    if (this.lockScroll) {
      enableBodyScroll(this)
    }
  }

  getTransitionTimeMs() {
    const transitionTimeValue = window.getComputedStyle(this)
      ?.getPropertyValue('--modal-transition-duration')?.replace(/\s+/g, '') || '0s';

    const match = transitionTimeValue.match(/^(-?\d*\.?\d+)(ms|s)$/);

    const transitionTimeMs = match
      ? match[2] === 'ms'
        ? parseFloat(match[1])
        : parseFloat(match[1]) * 1000
      : 0;

    return transitionTimeMs;
  }

  close(event) {
    if (event.type === 'click') {
      this.open = false
      return
    }
    event.code.toUpperCase() === 'ESCAPE' && (this.open = false)
  }

  toggleCart(e) {
    e?.detail?.event?.preventDefault()
    utils.toggle(document.body, ['mini-cart-is-active'])
    this.open = false
  }

  showCloseButton() {
    if (this.showClose === 'true') {
      return html`
        <button
          @click="${this.close}"
          class="button close"
          type="button"
          aria-label="${t('accessibility.close')}"
        >
          ${unsafeHTML(paths.close)}
        </button>
      `
    }
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      ?inert="${!this.open}"
    >
      ${this.showCloseButton()}
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="0"
      >
        <hh-focus-trap active="${this.open}" focus-delay="${this.transitionDelay}">
          <slot @toggleCart="${this.toggleCart}"></slot>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      background-color: var(--hh-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .button {
      width: 4rem;
      position: fixed;
      top: 4rem;
      right: 4rem;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 2;
    }

    .button.close {
      position: absolute;
      width: 36px;
      height: 36px;
      padding: 0;
      border: none;
      border-radius: 100px;
      background-color: var(--theme-color-reverse);
      color: var(--theme-color-ink);
      box-shadow: 0 4px 4px 0px rgba(0, 0, 0, 0.1);
    }

    .button.close:hover,
    .button.close:focus,
    .button.close:focus:not(:focus-visible) {
      color: var(--theme-color-reverse);
      background-color: var(--theme-color-ink);
      box-shadow: none;
    }

    .button.close:active {
      color: var(--theme-color-ink);
      background-color: var(--theme-color-reverse);
      box-shadow: none;
    }

    .button.close svg {
      height: 12px;
      width: 12px;
      transform: none;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100dvh;
      overflow: auto;
      width: 100%;
      padding: var(--theme-padding);
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }
  `
}

customElements.define('hh-modal', HHModal)
